import type { Locale } from '@/lib/i18n/config'
import { getMessages } from '@/lib/i18n/messages'

import type { EventLocationSlug } from '@/collections/Events/locationSlugs'
import { eventLocationSlugs } from '@/collections/Events/locationSlugs'
import type { PromotionLocationFilter } from '@/lib/promotions/promotionsQuery'

export function promotionCoversAllLocations(
  locations: EventLocationSlug[] | null | undefined,
): boolean {
  if (!locations?.length) {
    return false
  }

  return eventLocationSlugs.every((slug) => locations.includes(slug))
}

export function getPromotionLocationBadgeLabels(
  locations: EventLocationSlug[] | null | undefined,
  labels: Record<PromotionLocationFilter, string>,
): string[] {
  if (!locations?.length) {
    return []
  }

  if (promotionCoversAllLocations(locations)) {
    return [labels.all]
  }

  return locations
    .map((slug) => labels[slug])
    .filter((label): label is string => Boolean(label))
}

export type PromotionLocationDisplay = {
  city: string
  location?: string
}

export type PromotionLocationDisplayMap = Record<PromotionLocationFilter, PromotionLocationDisplay>

export function formatPromotionLocationLabel(
  display: PromotionLocationDisplay | undefined,
): string | undefined {
  if (!display) {
    return undefined
  }

  if (!display.location) {
    return display.city
  }

  return `${display.city}, ${display.location}`
}

export function buildPromotionLocationDisplayFromLocale(
  locale: Locale,
): PromotionLocationDisplayMap {
  const copy = getMessages(locale).promotions.hero

  return {
    all: { city: copy.locationAll },
    'berlin-mitte-wedding': copy.locations['berlin-mitte-wedding'],
    'berlin-frankfurter-tor': copy.locations['berlin-frankfurter-tor'],
    'berlin-neukoelln': copy.locations['berlin-neukoelln'],
    'berlin-adlershof': copy.locations['berlin-adlershof'],
    'frankfurt-riedberg': copy.locations['frankfurt-riedberg'],
    tuebingen: copy.locations.tuebingen,
    'cologne-k115': copy.locations['cologne-k115'],
  }
}

export function buildPromotionLocationLabels(
  locationDisplay: PromotionLocationDisplayMap,
): Record<PromotionLocationFilter, string> {
  return Object.fromEntries(
    Object.entries(locationDisplay).map(([key, value]) => [
      key,
      formatPromotionLocationLabel(value as PromotionLocationDisplay) ?? '',
    ]),
  ) as Record<PromotionLocationFilter, string>
}
